// asylumEngine.js — MiST · LAST ASYLUM (top-down survival-horror walking sim).
// Third standalone recorder genre. A dimly lit industrial grid explored under a
// tracking flashlight cone light-mask: 8-way movement, wall/debris collision,
// locked doors that consume keys, and glowing canisters/keys to recover. Owns
// its own RAF loop + keyboard/pointer input; no dependency on other engines.

const DPR = Math.max(1, Math.min(3, typeof window !== "undefined" ? window.devicePixelRatio : 1));

const TILE = 52;
const COLS = 22, ROWS = 22;
const WORLD_W = COLS * TILE, WORLD_H = ROWS * TILE;
const rnd = (a, b) => a + Math.random() * (b - a);
const ri = (a, b) => Math.floor(rnd(a, b + 1));
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

const LAYOUT_THEME = {
  wood:    { floor: "#1a1712", floor2: "#231d16", wall: "#3a2c1d", edge: "#5a4227", tint: "rgba(255,180,90,0.06)" },
  ice:     { floor: "#0e1620", floor2: "#12202e", wall: "#20323f", edge: "#3d5e6e", tint: "rgba(120,200,255,0.07)" },
  shelter: { floor: "#141416", floor2: "#1c1c20", wall: "#2c2c32", edge: "#4a4a54", tint: "rgba(200,210,255,0.05)" },
};

export class AsylumEngine {
  constructor(canvas, {
    onUpdate, onFinish, onGameOver, sprites, autopilot, durationMs,
    pacing, layout, danger, speed,
  } = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.images = new Map();
    this.backdrop = null;
    this.sprites = sprites || {};
    if (this.sprites.hero) this._img(this.sprites.hero);

    this.onUpdate = onUpdate || (() => {});
    this.onFinish = onFinish || (() => {});
    this.onGameOver = onGameOver || (() => {});

    this.autopilot = !!autopilot;
    this.durationMs = durationMs || 0;
    this.pacing = pacing || "night";            // daylight | night
    this.layout = LAYOUT_THEME[layout] ? layout : "shelter";
    this.danger = danger || "calm";             // calm | threat
    this.theme = LAYOUT_THEME[this.layout];

    // light-mask tuning
    this.darkness = this.pacing === "daylight" ? 0.55 : 0.94;
    this.coneR = this.pacing === "daylight" ? 340 : 230;
    this.ambientR = this.pacing === "daylight" ? 140 : 74;

    this.moveSpeed = (speed || 78) * 1.6;        // px / sec
    this.facing = -Math.PI / 2;                  // up
    this.vx = 0; this.vy = 0;
    this.items = 0; this.health = 100;
    this.keysCollected = 0; this.totalKeysInLevel = 0;
    this.exit = null; this.levelComplete = false; this.winBannerUntil = 0; this._finished = false; this._lockFloatAt = 0;
    this.floats = [];
    this.over = false; this._t0 = 0;
    this.walls = new Set();                      // "c,r"
    this.doors = []; this.pickups = []; this.threats = []; this.particles = [];
    this._aiTarget = null; this._aiRetargetAt = 0;

    this.w = 0; this.h = 0; this.raf = null; this.last = 0;
    this._keyState = {};
    this._buildWorld();

    this._onKeyDown = this._onKeyDown.bind(this);
    this._onKeyUp = this._onKeyUp.bind(this);
    this._down = this._down.bind(this);
    this._move = this._move.bind(this);
    this._up = this._up.bind(this);
    this._loop = this._loop.bind(this);
  }

  // ── World generation ────────────────────────────────────────────────────────
  _key(c, r) { return `${c},${r}`; }
  _isWall(c, r) { return c < 0 || r < 0 || c >= COLS || r >= ROWS || this.walls.has(this._key(c, r)); }
  _buildWorld() {
    // border + scattered debris walls, leaving an open interior.
    for (let c = 0; c < COLS; c++) { this.walls.add(this._key(c, 0)); this.walls.add(this._key(c, ROWS - 1)); }
    for (let r = 0; r < ROWS; r++) { this.walls.add(this._key(0, r)); this.walls.add(this._key(COLS - 1, r)); }
    // partial partition walls (industrial corridors) with gaps
    for (let i = 0; i < 5; i++) {
      const horiz = Math.random() < 0.5;
      const at = ri(4, (horiz ? ROWS : COLS) - 5);
      const gap = ri(3, (horiz ? COLS : ROWS) - 4);
      for (let j = 3; j < (horiz ? COLS : ROWS) - 3; j++) {
        if (Math.abs(j - gap) <= 1) continue;
        this.walls.add(horiz ? this._key(j, at) : this._key(at, j));
      }
    }
    // random debris chunks
    for (let i = 0; i < 24; i++) this.walls.add(this._key(ri(2, COLS - 3), ri(2, ROWS - 3)));

    const openCell = () => { let c, r, g = 0; do { c = ri(2, COLS - 3); r = ri(2, ROWS - 3); g++; } while (this._isWall(c, r) && g < 80); return { c, r }; };

    // player start (guaranteed open)
    const start = openCell();
    this.walls.delete(this._key(start.c, start.r));
    this.px = (start.c + 0.5) * TILE; this.py = (start.r + 0.5) * TILE;

    // keys + glowing canisters (item recovery) + locked doors
    for (let i = 0; i < 4; i++) { const o = openCell(); this.walls.delete(this._key(o.c, o.r)); this.pickups.push({ type: "key", x: (o.c + 0.5) * TILE, y: (o.r + 0.5) * TILE, got: false, ph: rnd(0, 7) }); }
    for (let i = 0; i < 5; i++) { const o = openCell(); this.walls.delete(this._key(o.c, o.r)); this.pickups.push({ type: "canister", x: (o.c + 0.5) * TILE, y: (o.r + 0.5) * TILE, got: false, ph: rnd(0, 7) }); }
    for (let i = 0; i < 4; i++) { const o = openCell(); this.walls.delete(this._key(o.c, o.r)); this.doors.push({ x: (o.c + 0.5) * TILE, y: (o.r + 0.5) * TILE, c: o.c, r: o.r, locked: true }); }

    // Key registry + exit portal: totalKeysInLevel = every key sprite scattered;
    // the exit gate sits at the farthest reachable node from the player start.
    this.totalKeysInLevel = this.pickups.filter((p) => p.type === "key").length;
    let ex = null, exd = -1;
    for (let c = 1; c < COLS - 1; c++) for (let r = 1; r < ROWS - 1; r++) {
      if (this._isWall(c, r)) continue;
      const d = Math.abs(c - start.c) + Math.abs(r - start.r);
      if (d > exd) { exd = d; ex = { c, r }; }
    }
    if (ex) { this.walls.delete(this._key(ex.c, ex.r)); this.exit = { x: (ex.c + 0.5) * TILE, y: (ex.r + 0.5) * TILE }; }

    if (this.danger === "threat") {
      for (let i = 0; i < 3; i++) { const o = openCell(); this.threats.push({ x: (o.c + 0.5) * TILE, y: (o.r + 0.5) * TILE, dir: rnd(0, 7), retarget: 0 }); }
    }
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────────
  mount() {
    this.resize();
    this.canvas.addEventListener("pointerdown", this._down);
    this.canvas.addEventListener("pointermove", this._move);
    window.addEventListener("pointerup", this._up);
    window.addEventListener("keydown", this._onKeyDown);
    window.addEventListener("keyup", this._onKeyUp);
    this.last = performance.now(); this._t0 = this.last;
    this._emit();
    this.raf = requestAnimationFrame(this._loop);
  }
  unmount() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.canvas.removeEventListener("pointerdown", this._down);
    this.canvas.removeEventListener("pointermove", this._move);
    window.removeEventListener("pointerup", this._up);
    window.removeEventListener("keydown", this._onKeyDown);
    window.removeEventListener("keyup", this._onKeyUp);
  }
  resize() {
    const r = this.canvas.getBoundingClientRect();
    this.w = r.width; this.h = r.height;
    this.canvas.width = Math.round(r.width * DPR);
    this.canvas.height = Math.round(r.height * DPR);
    this.ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
  }
  setBackdrop(url) { this.backdrop = url || null; if (url) this._img(url); }
  _img(url) {
    if (!url) return null;
    let im = this.images.get(url);
    if (!im) { im = new Image(); im.__r = false; im.onload = () => { im.__r = true; }; im.src = url; this.images.set(url, im); }
    return im.__r ? im : null;
  }
  _emit() { this.onUpdate({ keys: this.keysCollected, total: this.totalKeysInLevel, items: this.items, health: Math.round(this.health), levelComplete: this.levelComplete }); }
  _float(x, y, text, color) { this.floats.push({ x, y, text, color, life: 1 }); }

  // ── Input ─────────────────────────────────────────────────────────────────────
  _onKeyDown(e) {
    const k = e.key.toLowerCase();
    if (["arrowup", "arrowdown", "arrowleft", "arrowright", "w", "a", "s", "d"].includes(k)) e.preventDefault();
    this._keyState[k] = true;
  }
  _onKeyUp(e) { this._keyState[e.key.toLowerCase()] = false; }
  _down(e) { this.dragging = true; this._pointTarget(e); }
  _move(e) { if (this.dragging) this._pointTarget(e); }
  _up() { this.dragging = false; this._touchVec = null; }
  _pointTarget(e) {
    const rect = this.canvas.getBoundingClientRect();
    const dx = (e.clientX - rect.left) - this.w / 2;
    const dy = (e.clientY - rect.top) - this.h / 2;
    const m = Math.hypot(dx, dy) || 1;
    this._touchVec = { x: dx / m, y: dy / m };
  }

  // ── Movement + collision ─────────────────────────────────────────────────────
  _tryMove(nx, ny) {
    const R = 13;
    // resolve X then Y against wall tiles
    let x = this.px, y = this.py;
    const blocked = (tx, ty) => {
      const c0 = Math.floor((tx - R) / TILE), c1 = Math.floor((tx + R) / TILE);
      const r0 = Math.floor((ty - R) / TILE), r1 = Math.floor((ty + R) / TILE);
      for (let c = c0; c <= c1; c++) for (let r = r0; r <= r1; r++) if (this._isWall(c, r)) return true;
      // locked doors act as walls until unlocked
      for (const d of this.doors) if (d.locked && Math.hypot(tx - d.x, ty - d.y) < R + 16) return true;
      return false;
    };
    if (!blocked(nx, y)) x = nx;
    if (!blocked(x, ny)) y = ny;
    this.px = clamp(x, TILE, WORLD_W - TILE);
    this.py = clamp(y, TILE, WORLD_H - TILE);
  }

  _autoDrive(sec) {
    const now = performance.now();
    if (!this._aiTarget || this._aiTarget.done || now > this._aiRetargetAt) {
      let best = null, bd = 1e9;
      if (this.keysCollected >= this.totalKeysInLevel && this.exit) {
        // all keys in hand → make for the exit portal
        best = { x: this.exit.x, y: this.exit.y };
      } else {
        // prioritise uncollected KEYS (win path), then any other pickup
        for (const p of this.pickups) if (!p.got && p.type === "key") { const d = Math.hypot(p.x - this.px, p.y - this.py); if (d < bd) { bd = d; best = { x: p.x, y: p.y }; } }
        if (!best) for (const p of this.pickups) if (!p.got) { const d = Math.hypot(p.x - this.px, p.y - this.py); if (d < bd) { bd = d; best = { x: p.x, y: p.y }; } }
      }
      this._aiTarget = best || { x: rnd(TILE * 2, WORLD_W - TILE * 2), y: rnd(TILE * 2, WORLD_H - TILE * 2) };
      this._aiRetargetAt = now + 2600;
    }
    let dx = this._aiTarget.x - this.px, dy = this._aiTarget.y - this.py;
    const m = Math.hypot(dx, dy) || 1;
    if (m < 8) { this._aiTarget = null; return { x: 0, y: 0 }; }
    dx /= m; dy /= m;
    // if forward is blocked, try steering sideways so the walk keeps flowing
    const R = 14, ahead = (ox, oy) => { const c = Math.floor((this.px + ox) / TILE), r = Math.floor((this.py + oy) / TILE); return this._isWall(c, r); };
    if (ahead(dx * (R + 10), dy * (R + 10))) { const t = dx; dx = -dy; dy = t; }
    return { x: dx, y: dy };
  }

  _update(dt) {
    if (this.over) return;
    const sec = dt / 1000;
    const now = performance.now();
    if (this.durationMs && this._t0 && (now - this._t0) >= this.durationMs) { this._finish(this.levelComplete); return; }
    // hold on the victory banner briefly, then end the clip
    if (this.levelComplete) {
      for (const f of this.floats) { f.y -= 18 * sec; f.life -= sec * 0.9; }
      this.floats = this.floats.filter((f) => f.life > 0);
      if (now >= this.winBannerUntil) this._finish(true);
      return;
    }

    // resolve input vector
    let ix = 0, iy = 0;
    if (this.autopilot) { const a = this._autoDrive(sec); ix = a.x; iy = a.y; }
    else {
      const K = this._keyState;
      if (K["w"] || K["arrowup"]) iy -= 1;
      if (K["s"] || K["arrowdown"]) iy += 1;
      if (K["a"] || K["arrowleft"]) ix -= 1;
      if (K["d"] || K["arrowright"]) ix += 1;
      if ((ix || iy) === 0 && this._touchVec) { ix = this._touchVec.x; iy = this._touchVec.y; }
      const m = Math.hypot(ix, iy); if (m > 1) { ix /= m; iy /= m; }
    }
    if (ix || iy) {
      this.facing = Math.atan2(iy, ix);
      this._tryMove(this.px + ix * this.moveSpeed * sec, this.py + iy * this.moveSpeed * sec);
    }

    // key / canister pickup overlap → registry update + floating indicator
    for (const p of this.pickups) {
      if (p.got) continue;
      if (Math.hypot(p.x - this.px, p.y - this.py) < 26) {
        p.got = true;
        if (p.type === "key") { this.keysCollected += 1; this._float(p.x, p.y - 20, `🔑 KEY ${this.keysCollected}/${this.totalKeysInLevel}`, "#ffd23f"); }
        else { this.items += 1; this.health = Math.min(100, this.health + 6); this._float(p.x, p.y - 20, "▣ ITEM +1", "#39ff9e"); }
        this._burst(p.x, p.y, p.type === "key" ? "#ffd23f" : "#39ff9e");
        this._emit();
      }
    }
    // interior doors open freely on contact (keys are reserved for the exit)
    for (const d of this.doors) {
      if (d.locked && Math.hypot(d.x - this.px, d.y - this.py) < 40) { d.locked = false; this._burst(d.x, d.y, "#ff6a3d"); }
    }
    // exit portal — escape if every key is collected, else a red LOCKED float
    if (this.exit && Math.hypot(this.exit.x - this.px, this.exit.y - this.py) < 36) {
      if (this.keysCollected >= this.totalKeysInLevel) {
        this.levelComplete = true; this.winBannerUntil = now + 1400;
        this._float(this.exit.x, this.exit.y - 30, "✔ ESCAPED!", "#39ff9e");
        this._burst(this.exit.x, this.exit.y, "#39ff9e", 26);
        this._emit();
      } else if (now > this._lockFloatAt) {
        this._lockFloatAt = now + 700;
        this._float(this.exit.x, this.exit.y - 34, `🔒 LOCKED · ${this.totalKeysInLevel - this.keysCollected} KEYS LEFT`, "#ff4a4a");
      }
    }

    // threats
    if (this.danger === "threat") {
      for (const t of this.threats) {
        if (now > t.retarget) { t.dir = Math.atan2(this.py - t.y, this.px - t.x) + rnd(-0.6, 0.6); t.retarget = now + 900; }
        t.x += Math.cos(t.dir) * 46 * sec; t.y += Math.sin(t.dir) * 46 * sec;
        t.x = clamp(t.x, TILE, WORLD_W - TILE); t.y = clamp(t.y, TILE, WORLD_H - TILE);
        if (Math.hypot(t.x - this.px, t.y - this.py) < 26) {
          this.health -= 22 * sec; this._emit();
          if (this.health <= 0 && !this.autopilot) { this.over = true; this.onGameOver({ items: this.items }); return; }
        }
      }
    }
    if (this.pacing === "night") { this.health = Math.max(this.autopilot ? 20 : 0, this.health - 0.6 * sec); }

    for (const f of this.floats) { f.y -= 20 * sec; f.life -= sec * 0.8; }
    this.floats = this.floats.filter((f) => f.life > 0);
    for (const pt of this.particles) { pt.x += pt.vx * sec; pt.y += pt.vy * sec; pt.life -= sec * 1.5; }
    this.particles = this.particles.filter((p) => p.life > 0);
  }

  _finish(win) {
    if (this._finished) return;
    this._finished = true; this.over = true;
    this.onFinish({ keys: this.keysCollected, total: this.totalKeysInLevel, items: this.items, escaped: !!win });
  }

  _burst(x, y, color, n = 14) {
    for (let i = 0; i < n; i++) { const a = Math.random() * 7, s = rnd(30, 150); this.particles.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: 1, color }); }
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  _cam() {
    return {
      x: clamp(this.px - this.w / 2, 0, Math.max(0, WORLD_W - this.w)),
      y: clamp(this.py - this.h / 2, 0, Math.max(0, WORLD_H - this.h)),
    };
  }
  _render() {
    const ctx = this.ctx, w = this.w, h = this.h, th = this.theme;
    const cam = this._cam();
    const sx = (wx) => wx - cam.x, sy = (wy) => wy - cam.y;
    ctx.clearRect(0, 0, w, h);

    // floor grid (industrial checker)
    const c0 = Math.floor(cam.x / TILE), c1 = Math.ceil((cam.x + w) / TILE);
    const r0 = Math.floor(cam.y / TILE), r1 = Math.ceil((cam.y + h) / TILE);
    for (let c = c0; c <= c1; c++) for (let r = r0; r <= r1; r++) {
      const x = sx(c * TILE), y = sy(r * TILE);
      if (this._isWall(c, r)) {
        ctx.fillStyle = th.wall; ctx.fillRect(x, y, TILE, TILE);
        ctx.fillStyle = th.edge; ctx.fillRect(x, y, TILE, 3); ctx.fillRect(x, y, 3, TILE);
        ctx.fillStyle = "rgba(0,0,0,0.35)"; ctx.fillRect(x + TILE - 3, y, 3, TILE); ctx.fillRect(x, y + TILE - 3, TILE, 3);
      } else {
        ctx.fillStyle = (c + r) % 2 === 0 ? th.floor : th.floor2; ctx.fillRect(x, y, TILE, TILE);
        ctx.strokeStyle = "rgba(0,0,0,0.4)"; ctx.lineWidth = 1; ctx.strokeRect(x + 0.5, y + 0.5, TILE - 1, TILE - 1);
      }
    }

    // doors
    for (const d of this.doors) {
      const x = sx(d.x), y = sy(d.y);
      if (d.locked) {
        ctx.fillStyle = "#5a1d1d"; ctx.fillRect(x - 22, y - 26, 44, 52);
        ctx.strokeStyle = "#ff5a4a"; ctx.lineWidth = 2; ctx.strokeRect(x - 22, y - 26, 44, 52);
        ctx.fillStyle = "#ffcf3f"; ctx.beginPath(); ctx.arc(x, y, 5, 0, 7); ctx.fill();
        ctx.fillRect(x - 2, y, 4, 8);
      } else {
        ctx.strokeStyle = "#3aff9e"; ctx.lineWidth = 2; ctx.strokeRect(x - 22, y - 26, 44, 52);
        ctx.strokeStyle = "rgba(58,255,158,0.4)"; ctx.beginPath(); ctx.moveTo(x - 22, y - 26); ctx.lineTo(x + 22, y + 26); ctx.stroke();
      }
    }

    // exit portal — locked gate (red) until all keys collected, then green/open
    if (this.exit) {
      const x = sx(this.exit.x), y = sy(this.exit.y);
      const ready = this.keysCollected >= this.totalKeysInLevel;
      const pulse = 0.6 + 0.4 * Math.sin(performance.now() / 220);
      ctx.save(); ctx.shadowBlur = 20 * pulse; ctx.shadowColor = ready ? "#39ff9e" : "#ff4a4a";
      ctx.fillStyle = ready ? "rgba(12,42,28,0.9)" : "rgba(48,14,14,0.9)";
      ctx.fillRect(x - 28, y - 34, 56, 68);
      ctx.strokeStyle = ready ? "#39ff9e" : "#ff5a4a"; ctx.lineWidth = 3; ctx.strokeRect(x - 28, y - 34, 56, 68);
      ctx.shadowBlur = 0;
      // EXIT chevrons / lock icon
      ctx.fillStyle = ready ? "#39ff9e" : "#ff8a7a"; ctx.font = "900 11px 'Barlow Condensed', monospace"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText(ready ? "EXIT" : "LOCKED", x, y + 24);
      if (ready) { ctx.strokeStyle = "#39ff9e"; ctx.lineWidth = 3; for (let i = 0; i < 3; i++) { const yy = y - 8 + i * 8; ctx.beginPath(); ctx.moveTo(x - 10, yy + 6); ctx.lineTo(x, yy); ctx.lineTo(x + 10, yy + 6); ctx.stroke(); } }
      else { ctx.fillStyle = "#ffcf3f"; ctx.beginPath(); ctx.arc(x, y - 4, 7, Math.PI, 0); ctx.stroke(); ctx.fillRect(x - 9, y - 4, 18, 16); }
      ctx.restore();
    }
    for (const p of this.pickups) {
      if (p.got) continue;
      const x = sx(p.x), y = sy(p.y), pulse = 0.6 + 0.4 * Math.sin(performance.now() / 260 + p.ph);
      ctx.save(); ctx.shadowBlur = 16 * pulse;
      if (p.type === "key") {
        ctx.shadowColor = "#ffd23f"; ctx.fillStyle = "#ffd23f";
        ctx.beginPath(); ctx.arc(x - 4, y, 5, 0, 7); ctx.fill(); ctx.fillRect(x - 1, y - 2, 12, 4); ctx.fillRect(x + 8, y - 2, 3, 7);
      } else {
        ctx.shadowColor = "#39ff9e"; ctx.fillStyle = "#0b2a1c"; ctx.strokeStyle = "#39ff9e"; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.roundRect(x - 7, y - 12, 14, 24, 5); ctx.fill(); ctx.stroke();
        ctx.fillStyle = "#39ff9e"; ctx.globalAlpha = pulse; ctx.fillRect(x - 4, y - 6, 8, 12); ctx.globalAlpha = 1;
      }
      ctx.restore();
    }

    // threats
    for (const t of this.threats) {
      const x = sx(t.x), y = sy(t.y);
      ctx.fillStyle = "#20110f"; ctx.beginPath(); ctx.arc(x, y, 13, 0, 7); ctx.fill();
      ctx.fillStyle = "#ff2e2e"; ctx.beginPath(); ctx.arc(x - 4, y - 3, 2.4, 0, 7); ctx.arc(x + 4, y - 3, 2.4, 0, 7); ctx.fill();
    }

    // particles
    for (const pt of this.particles) { ctx.globalAlpha = Math.max(0, pt.life); ctx.fillStyle = pt.color; ctx.beginPath(); ctx.arc(sx(pt.x), sy(pt.y), 2.5, 0, 7); ctx.fill(); }
    ctx.globalAlpha = 1;

    // player sprite
    const ppx = sx(this.px), ppy = sy(this.py);
    const heroImg = this._img(this.sprites.hero);
    ctx.save(); ctx.shadowBlur = 6; ctx.shadowColor = "#000";
    if (heroImg) { const s = 34; ctx.drawImage(heroImg, ppx - s / 2, ppy - s / 2, s, s); }
    else {
      ctx.fillStyle = "rgba(0,0,0,0.45)"; ctx.beginPath(); ctx.ellipse(ppx, ppy + 12, 12, 5, 0, 0, 7); ctx.fill();
      ctx.fillStyle = "#cfe0ec"; ctx.beginPath(); ctx.arc(ppx, ppy, 11, 0, 7); ctx.fill();
      ctx.fillStyle = "#7d94a6"; ctx.beginPath(); ctx.arc(ppx, ppy, 6, 0, 7); ctx.fill();
      ctx.strokeStyle = "#0c1116"; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(ppx, ppy); ctx.lineTo(ppx + Math.cos(this.facing) * 12, ppy + Math.sin(this.facing) * 12); ctx.stroke();
    }
    ctx.restore();

    // ── flashlight light-mask (darken all, cut a tracking cone + ambient ring) ──
    ctx.save();
    ctx.fillStyle = `rgba(3,4,7,${this.darkness})`;
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = "destination-out";
    const amb = ctx.createRadialGradient(ppx, ppy, 4, ppx, ppy, this.ambientR);
    amb.addColorStop(0, "rgba(0,0,0,1)"); amb.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = amb; ctx.beginPath(); ctx.arc(ppx, ppy, this.ambientR, 0, 7); ctx.fill();
    const cone = ctx.createRadialGradient(ppx, ppy, 6, ppx, ppy, this.coneR);
    cone.addColorStop(0, "rgba(0,0,0,1)"); cone.addColorStop(0.62, "rgba(0,0,0,0.82)"); cone.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = cone;
    const spread = 0.42;
    ctx.beginPath(); ctx.moveTo(ppx, ppy);
    ctx.arc(ppx, ppy, this.coneR, this.facing - spread, this.facing + spread); ctx.closePath(); ctx.fill();
    ctx.restore();

    // warm beam tint over the cone
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    const beam = ctx.createRadialGradient(ppx, ppy, 6, ppx, ppy, this.coneR);
    beam.addColorStop(0, "rgba(255,240,200,0.14)"); beam.addColorStop(1, "rgba(255,240,200,0)");
    ctx.fillStyle = beam;
    ctx.beginPath(); ctx.moveTo(ppx, ppy); ctx.arc(ppx, ppy, this.coneR, this.facing - spread, this.facing + spread); ctx.closePath(); ctx.fill();
    // theme atmosphere tint
    ctx.globalCompositeOperation = "source-over"; ctx.fillStyle = th.tint; ctx.fillRect(0, 0, w, h);
    ctx.restore();

    // danger vignette pulse
    if (this.danger === "threat") {
      const near = this.threats.some((t) => Math.hypot(t.x - this.px, t.y - this.py) < 180);
      if (near) { ctx.save(); ctx.globalAlpha = 0.22 + 0.14 * Math.sin(performance.now() / 120); ctx.strokeStyle = "#ff1f1f"; ctx.lineWidth = 16; ctx.strokeRect(0, 0, w, h); ctx.restore(); }
    }

    // floating pickup / lock indicators (world-anchored, above the darkness)
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    for (const f of this.floats) {
      ctx.save(); ctx.globalAlpha = Math.max(0, f.life);
      ctx.font = "900 13px 'Barlow Condensed', monospace"; ctx.shadowBlur = 6; ctx.shadowColor = "#000";
      ctx.fillStyle = f.color; ctx.fillText(f.text, sx(f.x), sy(f.y));
      ctx.restore();
    }

    // HUD status (above the darkness)
    ctx.fillStyle = "#ffd23f"; ctx.font = "900 14px 'Barlow Condensed', monospace"; ctx.textAlign = "left"; ctx.textBaseline = "top";
    ctx.fillText(`🔑 ${this.keysCollected}/${this.totalKeysInLevel}   ▣ ${this.items}`, 10, 10);
    ctx.fillStyle = "rgba(255,255,255,0.2)"; ctx.fillRect(10, 30, 90, 6);
    ctx.fillStyle = this.health > 35 ? "#39ff9e" : "#ff4a4a"; ctx.fillRect(10, 30, 90 * clamp(this.health / 100, 0, 1), 6);
    // objective line
    ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.font = "700 10px 'Barlow Condensed', monospace";
    ctx.fillText(this.keysCollected >= this.totalKeysInLevel ? "OBJECTIVE: REACH THE EXIT" : "OBJECTIVE: COLLECT ALL KEYS", 10, 42);

    // victory banner
    if (this.levelComplete) {
      ctx.save();
      ctx.fillStyle = "rgba(4,20,12,0.72)"; ctx.fillRect(0, h * 0.4, w, h * 0.2);
      ctx.strokeStyle = "#39ff9e"; ctx.lineWidth = 2; ctx.strokeRect(0, h * 0.4, w, h * 0.2);
      const bp = 1 + 0.06 * Math.sin(performance.now() / 90);
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.shadowBlur = 22; ctx.shadowColor = "#39ff9e"; ctx.fillStyle = "#eafff3";
      ctx.font = `900 ${Math.round(26 * bp)}px 'Barlow Condensed', system-ui, sans-serif`;
      ctx.fillText("✔ ESCAPED THE ASYLUM", w / 2, h * 0.5);
      ctx.restore();
    }
  }

  _loop(now) {
    const dt = Math.min(50, now - this.last); this.last = now;
    this._update(dt); this._render();
    if (!this.over) this.raf = requestAnimationFrame(this._loop);
  }
}
